/* @flow */

import type { KeyValuePair } from '../jsHelpers/types';
import type LoadChecker from './loadChecker';
import type { CheckOutSuccessResponse } from './types';

import { HttpStatus } from '../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { logInfo, logWarning } from '../debug/debug';

const SITE_NAME: string = process.env.REACT_APP_CHARGEBEE_INSTANCE || '';

export default class ChargebeeWrapper {
  static checkOutSuccessUrl: ?string;

  static checkOutUrl: ?string;

  static instance: ?ChargebeeInstance;

  static portalUrl: ?string;

  static token: string;

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'ChargebeeWrapper';
  }

  static initialize: (token: string, urls: KeyValuePair<string>) => void = (token, urls) => {
    if (SITE_NAME === '') {
      logWarning('Cannot initialize Chargebee. Missing site name.');
      return;
    }

    if (ChargebeeWrapper.instance) {
      // Chargebee already initialized
      ChargebeeWrapper.updateToken(token);
      return;
    }

    const { checkOutSuccessUrl, checkOutUrl, customerAccountUrl } = urls;

    ChargebeeWrapper.checkOutSuccessUrl = `${checkOutSuccessUrl}2`;
    ChargebeeWrapper.checkOutUrl = checkOutUrl;
    ChargebeeWrapper.portalUrl = customerAccountUrl;
    ChargebeeWrapper.token = token;

    ChargebeeWrapper.instance = Chargebee.init({
      enableGTMTracking: true,
      isItemsModel: true,
      site: SITE_NAME,
    });
  };

  static destroy: () => void = () => {
    if (!ChargebeeWrapper.instance) {
      return;
    }

    ChargebeeWrapper.instance.logout();
    ChargebeeWrapper.instance = null;
    ChargebeeWrapper.token = '';
  };

  static showDebug: () => void = () => {
    const friendlyDisplay = (s: ?string | boolean): string => {
      if (s === undefined) {
        return '<undefined>';
      }
      if (s === null) {
        return '<null>';
      }
      return s.toString();
    };

    logInfo('---------- Chargebee Debug Info');
    logInfo(`portalUrl: ${friendlyDisplay(ChargebeeWrapper.portalUrl)}`);
    logInfo(`checkOutUrl: ${friendlyDisplay(ChargebeeWrapper.checkOutUrl)}`);
    logInfo(`checkOutSuccessUrl: ${friendlyDisplay(ChargebeeWrapper.checkOutSuccessUrl)}`);
    logInfo(`token: ${friendlyDisplay(ChargebeeWrapper.token)}`);
    logInfo('----------');
  };

  static getInstance: () => ChargebeeInstance = () => {
    if (!ChargebeeWrapper.instance) {
      throw new Error('Chargebee instance is missing');
    }

    return ChargebeeWrapper.instance;
  };

  static updateToken: (token: string) => void = (token) => {
    if (!ChargebeeWrapper.instance) {
      return;
    }

    ChargebeeWrapper.token = token;
  };

  static getFetchOptions: () => any = () => {
    return {
      headers: new Headers({
        Authorization: `Bearer ${ChargebeeWrapper.token}`,
        'Content-Type': 'application/json; charset=utf-8',
      }),
      method: 'POST',
    };
  };

  static openCheckOut: (vtiId: number, description: string, originalPrice: number, promocode: ?string, discountPrice: ?number, timer: LoadChecker) => Promise<CheckOutSuccessResponse | void> = (
    vtiId,
    description,
    originalPrice,
    promocode,
    discountPrice,
    timer,
  ) =>
    new Promise((resolve, reject) => {
      if (!ChargebeeWrapper.checkOutUrl) {
        throw new Error('Check out URL is missing');
      }

      ChargebeeWrapper.getInstance().openCheckout({
        close: () => {
          /*
           * User manually closed the hosted page:
           * If it happens at the end of the transaction, the app won't know if it was successful or not
           * But since the CB popup is closed just before calling purchaseSuccessCallback, the user is not supposed to be able to close it
           */
          timer.clear();
          resolve();
        },
        error: (error: Error) => {
          timer.clear();
          ChargebeeWrapper.getInstance().closeAll();
          reject(error);
        },
        hostedPage: async () => {
          const options = ChargebeeWrapper.getFetchOptions();
          const body = {
            description,
            identity: ChargebeeWrapper.token,
            listPrice: originalPrice,
            price: discountPrice !== undefined && discountPrice !== null ? discountPrice : originalPrice,
            promocode,
            vtiId: vtiId.toString(),
          };

          options.body = JSON.stringify(body);

          if (!ChargebeeWrapper.checkOutUrl) {
            throw new Error('Check out URL is missing');
          }

          try {
            const response = await fetch(ChargebeeWrapper.checkOutUrl, options);
            return await response.json();
          } finally {
            // Ignore any error since it will be handled later
            timer.clear();
          }
        },
        success: (hostedPageId) => {
          ChargebeeWrapper.purchaseSuccessCallback(hostedPageId).then(resolve).catch(reject);
        },
      });
    });

  static purchaseSuccessCallback: (hostedPageId: string) => Promise<CheckOutSuccessResponse> = async (hostedPageId) => {
    if (!ChargebeeWrapper.checkOutSuccessUrl) {
      throw new Error('Check out success URL is missing');
    }

    const url = new URL(ChargebeeWrapper.checkOutSuccessUrl);
    url.search = new URLSearchParams({ hostedPageId }).toString();

    // Prevent the user from closing the popup since, at this point, the transaction is successful
    ChargebeeWrapper.getInstance().closeAll();

    // Converting URL to string is useless, but Flow doesn't know the constructor accepting a URL
    const response = await fetch(url.toString(), ChargebeeWrapper.getFetchOptions());
    const data = await response.json();
    const { httpCode } = data;

    if (httpCode === HttpStatus.OK) {
      // Purchase is OK
      return data;
    }

    throw new Error(JSON.stringify(data));
  };

  static openPortal: (timer: LoadChecker) => Promise<void> = (timer) =>
    new Promise((resolve, reject) => {
      ChargebeeWrapper.getInstance().setPortalSession(() => {
        if (!ChargebeeWrapper.portalUrl) {
          throw new Error('Portal URL is missing');
        }

        return fetch(ChargebeeWrapper.portalUrl ?? '', ChargebeeWrapper.getFetchOptions())
          .then((data) => data.json())
          .catch(() => {
            // Ignore this error since it will be handled below
          });
      });

      const cbPortal = ChargebeeWrapper.getInstance().createChargebeePortal();

      cbPortal.open({
        close: () => ChargebeeWrapper.getInstance().logout(),
        error: (error: Error) => {
          timer.clear();
          ChargebeeWrapper.getInstance().logout();
          ChargebeeWrapper.getInstance().closeAll();
          reject(error);
        },
        loaded: timer.clear,
      });
    });
}
